import { useFormContext } from 'react-hook-form'
import { Grid, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Checkbox from 'components/generic/form/Checkbox'
import RadioGroup from 'components/generic/form/RadioGroup'
import Select from 'components/generic/form/Select'
import Input from 'components/generic/form/Input'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import SettingsAutoCloseWarning from 'components/layouts/settings/SettingsAutoCloseWarning'
import { get } from 'lodash'

interface Condition {
  key: string
  value: any
}

interface ComponentData {
  path: string
  label: string
  componentType: string
  items?: { id: string; name: string }[]
  labelInput?: string
  inputType?: string
  disabledBy?: Condition[]
  inputList?: any[]
  title?: string
  selectProps?: any
  dropdownOptions?: any[]
}

interface SettingsListProps {
  title: string
  subTitle: string
  subTitle2?: string
  listComponents: ComponentData[]
  itemsInRowDirection?: boolean
}

const SettingsList: React.FC<SettingsListProps> = ({
  title,
  subTitle,
  subTitle2,
  listComponents,
  itemsInRowDirection,
}) => {
  const theme = useTheme()
  const methods = useFormContext()

  const renderListComponents = componentData => {
    const disabled = componentData?.disabledBy?.some(condition => methods.getValues(condition.key) === condition.value)
    switch (componentData.componentType) {
      case 'checkbox':
        return (
          <Grid key={componentData.path} mt={2}>
            <Checkbox
              disabled={disabled}
              name={componentData.path}
              label={componentData.label}
              control={methods.control}
            />
          </Grid>
        )
      case 'radioGroup':
        return (
          <Grid key={componentData.path} mt={2}>
            <RadioGroup
              disabled={disabled}
              name={componentData.path}
              label={componentData.label}
              control={methods.control}
              defaultValue=""
              items={componentData.items}
            />
          </Grid>
        )
      case 'select':
        return (
          <Grid key={componentData.path} mt={2}>
            <Stack alignItems="center" direction="row">
              <Typography color={disabled ? theme.palette.text.disabled : 'unset'} mr={1}>
                {componentData.label}
              </Typography>
              <Select
                disabled={disabled}
                name={componentData.path}
                defaultValue={`${componentData.items[1]?.id}`}
                control={methods.control}
                items={componentData.items}
                sx={{}}
                selectProps={{
                  sx: { height: '40px', width: 'auto' },
                }}
              />
            </Stack>
          </Grid>
        )
      case 'multiSelect':
        return (
          <Grid key={componentData.path} mt={2}>
            <Stack alignItems="center" direction="row">
              <Typography color={disabled ? theme.palette.text.disabled : 'unset'} mr={1}>
                {componentData.label}
              </Typography>
              <Select
                label={componentData.labelInput}
                disabled={disabled}
                name={componentData.path}
                control={methods.control}
                items={componentData.items}
                sx={{}}
                selectProps={{ multiple: true, sx: { width: '400px' } }}
                useChips
              />
            </Stack>
          </Grid>
        )

      case 'input': {
        const inputFormatting = {
          inputType: componentData.inputType,
          allowLeadingZeros: true,
          allowNegative: false,
        }
        if (componentData.inputType === 'price') {
          inputFormatting['decimalScale'] = 2
        } else if (componentData.inputType === 'number') {
          inputFormatting['thousandSeparator'] = false
          inputFormatting['decimalScale'] = 0
        }
        const multiline = componentData.multiline ?? false
        const maxWidth = multiline ? 'auto' : '200px'
        const size = multiline ? 'medium' : 'small'

        return (
          <Grid key={componentData.path} mt={2}>
            <Stack alignItems="center" direction="row">
              {componentData.label && (
                <Typography color={disabled ? theme.palette.text.disabled : 'unset'} mr={1}>
                  {componentData.label}
                </Typography>
              )}
              <Input
                label={componentData.labelInput}
                disabled={disabled}
                name={componentData.path}
                defaultValue=""
                control={methods.control}
                size={size}
                sx={{ maxWidth }}
                InputProps={
                  componentData.inputType === 'number' || componentData.inputType === 'price'
                    ? {
                        inputComponent: InputFormat,
                        inputProps: inputFormatting,
                      }
                    : null
                }
                multiline={multiline}
              />
            </Stack>
          </Grid>
        )
      }
      case 'multiInputRow':
        return (
          <Grid key={componentData.title} mt={2}>
            <Stack direction="row">
              {componentData.inputList.map(field => {
                const standardFieldSettings = {
                  key: field.path,
                  name: field.path,
                  label: field.label,
                  size: field?.size || 'small',
                  variant: 'outlined',
                  'data-test': field.dataTest,
                  control: methods.control,
                  error: !!get(methods?.formState?.errors, field?.path),
                  helperText: get(methods.formState.errors, `${field.path}.message`),
                  defaultValue: field?.defaultValue || '',
                  onBlur: field?.onBlur,
                  onChange: field?.onChange,
                  disabled: !!field?.disabled,
                  sx: {
                    width: '200px',
                    marginRight: 1,
                  },
                }
                const thresholds = {
                  'purchaseSettings.payment.paymentThreshold': 'hasThreshold',
                  'purchaseSettings.payment.commodityType.ferrousCostThreshold': 'hasThreshold',
                  'purchaseSettings.payment.commodityType.nonFerrousCostThreshold': 'hasThreshold',
                }

                for (const [path, hasThresholdField] of Object.entries(thresholds)) {
                  if ([`${path}.ticketValueThreshold`, `${path}.allowedPaymentTypes`].includes(field.path)) {
                    standardFieldSettings.disabled = !methods.getValues(`${path}.${hasThresholdField}`)
                    break
                  }
                }

                switch (field.inputType) {
                  case 'number':
                    return (
                      <Input
                        {...standardFieldSettings}
                        InputProps={
                          field.inputType === 'number'
                            ? {
                                inputComponent: InputFormat,
                                inputProps: {
                                  inputType: 'price',
                                  allowLeadingZeros: true,
                                  allowNegative: false,
                                  decimalScale: 2,
                                },
                              }
                            : null
                        }
                      />
                    )
                  case 'dropdown':
                    return (
                      <Select
                        {...standardFieldSettings}
                        items={field.dropdownOptions}
                        selectProps={field.selectProps}
                        useChips
                      />
                    )
                  default:
                    return null
                }
              })}
            </Stack>
          </Grid>
        )
      case 'autoCloseWarning':
        return <SettingsAutoCloseWarning key={componentData.path} path={componentData.path} />
      default:
        return null
    }
  }

  return (
    <>
      <Typography id={title.replace(/\s+/g, '-').toLowerCase()} variant="h6" color="textPrimary">
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {subTitle}
      </Typography>
      {subTitle2 && (
        <Typography variant="body2" color="textSecondary">
          {subTitle2}
        </Typography>
      )}
      <Stack direction={itemsInRowDirection ? 'row' : 'column'}>{listComponents.map(renderListComponents)}</Stack>
    </>
  )
}

export default SettingsList
